/**
 * BrandCardItem - Single brand card
 *
 * Image-card layout for producer/brand artwork:
 * - fixed 2:1 card ratio
 * - uploaded image fills the full card
 * - optional title overlay sits bottom-right
 */

import { safeHref } from '../../lib'

import type { HomepageBrandCard } from '../../types'

const BRAND_CARD_CLASSES = 'group relative !mb-0 block aspect-[2/1] overflow-hidden rounded-lg border border-border-color bg-white text-text-color no-underline shadow-sm transition-colors duration-300 hover:border-text-color hover:text-text-color hover:no-underline visited:text-text-color active:text-text-color focus:text-text-color'
const BRAND_CARD_IMAGE_CLASSES = 'absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105'
const BRAND_CARD_FALLBACK_CLASSES = 'absolute inset-0 flex items-center justify-center bg-cards-bg text-sm text-second-btn-bg'
const BRAND_CARD_TITLE_CLASSES = 'absolute bottom-2 right-0 max-w-[88%] rounded-l-[200px] bg-main-color px-3 py-1 text-right font-main-font text-xs font-bold uppercase leading-tight tracking-wide text-text-color shadow-sm md:text-sm'

interface BrandCardItemProps {
  card: HomepageBrandCard
  /** Resolved URL for category link type */
  resolvedUrl: string | undefined
}

export function BrandCardItem({ card, resolvedUrl }: BrandCardItemProps) {
  const href =
    card.link_type === 'category'
      ? safeHref(resolvedUrl)
      : safeHref(card.link_value)

  if (!href) return null

  return (
    <a href={href} className={BRAND_CARD_CLASSES}>
      {card.image ? (
        <img
          src={card.image}
          alt={card.title || 'Brand'}
          width={600}
          height={300}
          className={BRAND_CARD_IMAGE_CLASSES}
          loading="lazy"
        />
      ) : (
        <span className={BRAND_CARD_FALLBACK_CLASSES}>No image</span>
      )}

      {card.title && <span className={BRAND_CARD_TITLE_CLASSES}>{card.title}</span>}
    </a>
  )
}
